/**
 * Barramento do softphone entre as abas do CRM.
 *
 * ── O problema que isto resolve (medido em 09/08/2026) ─────────────────────
 * O Asterisk aceita UM registro por ramal: `max_contacts=1` + `remove_existing=yes`
 * nos 54 ramais de /etc/asterisk/pjsip_users.conf. Cada aba do CRM criava o seu
 * próprio JsSIP e registrava o MESMO ramal — então toda aba nova CHUTAVA a
 * anterior.
 *
 * No log de segurança do Asterisk (212.177 autenticações):
 *   · 35.115 pares de sessões sobrepostas no mesmo ramal
 *   · pico de 25 abas simultâneas num ramal só (4018), 20 no 4006
 *   · a aba chutada só tenta de novo no ciclo seguinte — mediana 429s, p90 542s
 *
 * Daí os três sintomas que o Douglas relatou: o softphone fica vermelho, a
 * ligação cai no meio, e demora muito para voltar.
 *
 * ── A régua ────────────────────────────────────────────────────────────────
 * UMA aba segura o registro (a DONA). Todas as outras espelham o estado dela e
 * mandam comando por aqui. Resultado, exatamente como o Douglas pediu:
 *   · verde em TODAS as abas — é o status do único softphone
 *   · aperta ligar em QUALQUER aba e a ligação sai
 *   · NUNCA duas ligações ao mesmo tempo, porque só existe um softphone
 *
 * A posse é decidida pela Web Locks API, que o navegador libera sozinho quando
 * a aba fecha ou trava — então a próxima aba assume em seguida, sem ninguém
 * precisar recarregar nada.
 *
 * ⛔ Sem BroadcastChannel ou sem Web Locks (navegador antigo) o CRM volta ao
 * comportamento de antes: cada aba por si. É o pior caso, não uma regressão —
 * e por isso os dois são exigidos juntos em COORDENA_ABAS.
 */

/** Nome do lock que decide quem segura o registro SIP. */
const LOCK_DONO = "softphone-prime-dono";
/** Canal de estado e comandos entre as abas. */
const CANAL = "softphone-prime";

/** O navegador tem as duas peças? Sem as duas, cada aba volta a ser dona de si. */
export const COORDENA_ABAS =
  typeof BroadcastChannel !== "undefined" &&
  typeof navigator !== "undefined" &&
  typeof (navigator as unknown as { locks?: { request?: unknown } })?.locks?.request === "function";

/** O que a aba dona publica para as outras espelharem. */
export interface SoftphoneEstado {
  status: string;
  ramal: string | null;
  inCall: boolean;
  calling: boolean;
  incoming: boolean;
  incomingFrom: string | null;
  number: string;
  contactName: string | null;
  contactFoto: string | null;
  muted: boolean;
  timer: string;
  /** ⛔ Douglas 04/09/2026: "apertam o botão de desligar e não desliga...
   * demora". terminate() dispara o BYE na hora, mas a tela só limpava
   * `inCall` quando o evento "ended"/"failed" do JsSIP voltava — round-trip
   * de rede sem feedback nenhum no meio. Sem isto o clique parecia não ter
   * feito nada, e a pessoa clicava de novo (e de novo). */
  encerrando: boolean;
}

/** O que uma aba seguidora pede para a dona executar. */
export type SoftphoneCmd =
  | { c: "call"; numero: string; nome?: string; foto?: string | null }
  | { c: "hangup" }
  | { c: "answer" }
  | { c: "reject" }
  | { c: "mute" }
  | { c: "dtmf"; d: string }
  | { c: "transfer"; target: string };

export type SoftphoneMsg =
  | { t: "estado"; estado: SoftphoneEstado }
  | { t: "ola" }
  | { t: "cmd"; cmd: SoftphoneCmd }
  | { t: "falha"; titulo: string; descricao?: string };

export function criarCanal(): BroadcastChannel | null {
  if (!COORDENA_ABAS) return null;
  try {
    return new BroadcastChannel(CANAL);
  } catch {
    return null;
  }
}

/**
 * Entra na fila pela posse do softphone.
 *
 * `aoGanhar` roda quando esta aba vira a dona. O lock fica preso até a função
 * devolvida ser chamada (desmontagem) ou até a aba morrer — nos dois casos o
 * navegador passa a vez para a próxima da fila.
 *
 * Sem coordenação disponível, ganha na hora: é o comportamento antigo.
 */
export function disputarPosse(aoGanhar: () => void): () => void {
  if (!COORDENA_ABAS) {
    aoGanhar();
    return () => {};
  }

  const locks = (navigator as unknown as {
    locks: { request: (n: string, o: object, f: () => Promise<void>) => Promise<void> };
  }).locks;

  let soltar: (() => void) | null = null;
  let desistiu = false;
  const ctrl = new AbortController();

  locks
    .request(LOCK_DONO, { signal: ctrl.signal }, () =>
      new Promise<void>((resolve) => {
        // Desmontou antes de chegar a vez: devolve o lock sem assumir nada.
        if (desistiu) {
          resolve();
          return;
        }
        soltar = resolve;
        aoGanhar();
      }),
    )
    .catch(() => {
      /* abort = saiu da fila. Não é erro. */
    });

  return () => {
    desistiu = true;
    try {
      ctrl.abort();
    } catch {
      /* noop */
    }
    soltar?.();
  };
}
